import React, {useEffect, useRef, useState} from 'react';

import Icon, {Icons} from '@pb/kit-component-icon';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from '../../../scroll.component.module.styl';
import {getIconPosition} from '../../../utils/get-icon-position';

import type {OverlayScrollbars} from 'overlayscrollbars';
import type {ComponentPropsWithoutRef} from 'react';

const classes = withCssModule(styles);

type NativeProps = ComponentPropsWithoutRef<'div'>;

export interface ScrollArrowProps extends NativeProps {
    direction: 'left' | 'right',
    getScrollInstance: () => OverlayScrollbars | null,
    scrollStep?: number | ((currentScrolLeft: number) => number),
    parentScrollContainer?: HTMLElement | typeof window,
}

export function ScrollArrow({
    getScrollInstance,
    scrollStep = 100,
    direction,
    parentScrollContainer = window,
    onClick,
    ...restProps
}: ScrollArrowProps) {
    const arrowRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (!arrowRef.current) {
            return;
        }

        function positionIcon() {
            if (!iconRef.current || !arrowRef.current || !isHovered) {
                return;
            }

            const top = getIconPosition(
                arrowRef.current,
                parentScrollContainer,
            );
            iconRef.current.style.top = `${top}px`;
        }

        parentScrollContainer.addEventListener('scroll', positionIcon, {passive: true});
        parentScrollContainer.addEventListener('resize', positionIcon);
        const resizeObserver = new ResizeObserver(positionIcon);
        resizeObserver.observe(arrowRef.current);
        positionIcon();

        return () => {
            parentScrollContainer.removeEventListener('scroll', positionIcon);
            parentScrollContainer.removeEventListener('resize', positionIcon);
            resizeObserver.disconnect();
        };
    }, [parentScrollContainer, isHovered]);

    return (
        <div
            ref={arrowRef}
            {...restProps}
            aria-hidden={true}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={event => {
                const scrollLeft = getScrollInstance()?.elements().viewport.scrollLeft || 0;
                const finalScrollStep = typeof scrollStep === 'function' ? scrollStep(scrollLeft) : scrollStep;
                getScrollInstance()?.elements().viewport.scrollTo({
                    left: direction === 'left' ?
                        scrollLeft - finalScrollStep
                        : scrollLeft + finalScrollStep,
                });
                onClick?.(event);
            }}
            className={classes(`scroll-arrow scroll-arrow-${direction}`, restProps.className)}>
            <Icon
                ref={iconRef}
                className={classes('scroll-arrow-icon')}
                type={direction === 'left' ? Icons.CHEVRON_LEFT : Icons.CHEVRON_RIGHT}
            />
        </div>
    );
}
